import { Controller } from "@hotwired/stimulus"

// Command palette for paperwork process view
export default class extends Controller {
  static targets = ["backdrop", "panel", "input", "list", "emptyState", "status", "title", "loadingIndicator", "statusMessage", "content", "overlay", "overlayMessage"]

  static values = {
    commands: Array,
    updateUrl: String,
    processReference: String,
    processTitle: String,
    mode: String,
    bulkStateUrl: String,
    bulkAssignmentUrl: String,
    bulkReplyUrl: String,
    titleBase: String,
  }

  initialize() {
    this.selectedProcessIds = []
  }

  connect() {
    console.log("CommandPaletteController connected")
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this.previousActiveElement = null
    this.isOpen = false
    this.isExecutingCommand = false
    this.selectedProcessIds = []
    this.baseTitle = this.hasTitleBaseValue ? this.titleBaseValue : (this.hasTitleTarget ? this.titleTarget.textContent : "")

    this.allCommands = this.sortCommandsByRecency(this.commandsValue || [])
    this.filteredCommands = [...this.allCommands]
    this.selectedIndex = 0
    this.render()
    this.updateTitle()

    this.handleGlobalKeydown = this.handleGlobalKeydown.bind(this)
    window.addEventListener("keydown", this.handleGlobalKeydown)
  }

  disconnect() {
    window.removeEventListener("keydown", this.handleGlobalKeydown)
  }

  isBulkMode() {
    const mode = this.hasModeValue ? (this.modeValue || "") : ""
    return mode.toLowerCase() === "bulk"
  }

  updateSelection(event) {
    const ids = event?.detail?.ids

    if (Array.isArray(ids)) {
      this.selectedProcessIds = ids
        .map((id) => (id !== undefined && id !== null ? id.toString() : ""))
        .map((id) => id.trim())
        .filter((id) => id.length > 0)
    } else if (typeof ids === "string") {
      this.selectedProcessIds = ids
        .split(",")
        .map((id) => id.trim())
        .filter((id) => id.length > 0)
    } else {
      this.selectedProcessIds = []
    }

    if (this.isBulkMode()) {
      this.updateTitle()
      if (this.selectedProcessIds.length === 0) {
        this.hideStatus()
      }
    }
  }

  ensureBulkSelection() {
    if (!this.isBulkMode()) return true
    if (this.selectedProcessIds.length > 0) return true

    this.showError("Select one or more workflows to run that command.")
    return false
  }

  updateTitle() {
    if (!this.hasTitleTarget) return

    const configuredBase = this.hasTitleBaseValue ? this.titleBaseValue : this.baseTitle
    const computedBase = configuredBase && configuredBase.length > 0
      ? configuredBase
      : [
          this.hasProcessReferenceValue ? this.processReferenceValue : null,
          this.hasProcessTitleValue ? this.processTitleValue : null,
        ]
          .filter(Boolean)
          .join(" · ")

    let title = computedBase || ""

    if (this.isBulkMode()) {
      const count = Array.isArray(this.selectedProcessIds) ? this.selectedProcessIds.length : 0
      const base = (computedBase && computedBase.length > 0) ? computedBase : "Selected workflows"
      title = count > 0 ? `${base} (${count})` : base
    }

    this.titleTarget.textContent = title
  }

  commandsValueChanged() {
    this.allCommands = this.sortCommandsByRecency(this.commandsValue || [])
    this.filteredCommands = [...this.allCommands]
    this.selectedIndex = 0
    if (this.isOpen) this.render()
    this.updateTitle()
  }

  openFromButton(event) {
    event.preventDefault()
    this.open()
  }

  panelBackgroundClick(event) {
    if (event.target === event.currentTarget) {
      event.preventDefault()
      this.close()
    }
  }

  open() {
    if (this.isOpen) return

    this.isOpen = true
    this.previousActiveElement = document.activeElement
    this.hideLoadingOverlay()
    // Re-sort commands on open to reflect any recent usage
    this.allCommands = this.sortCommandsByRecency(this.allCommands)
    this.filteredCommands = [...this.allCommands]
    this.selectedIndex = 0
    this.hideStatus()
    this.updateTitle()
    this.render()

    if (this.hasBackdropTarget) this.backdropTarget.classList.remove("hidden")
    if (this.hasPanelTarget) this.panelTarget.classList.remove("hidden")

    window.requestAnimationFrame(() => {
      if (this.hasInputTarget) {
        this.inputTarget.value = ""
        this.inputTarget.focus()
      }
    })
  }

  close(event) {
    if (event) event.preventDefault()
    if (!this.isOpen) return

    this.isOpen = false
    this.hideLoadingOverlay()
    this.filteredCommands = [...this.allCommands]
    this.selectedIndex = 0
    this.hideStatus()
    this.render()

    if (this.hasBackdropTarget) this.backdropTarget.classList.add("hidden")
    if (this.hasPanelTarget) this.panelTarget.classList.add("hidden")

    if (this.previousActiveElement && typeof this.previousActiveElement.focus === "function") {
      this.previousActiveElement.focus({ preventScroll: true })
    }
    this.previousActiveElement = null
    this.updateTitle()
  }

  handleGlobalKeydown(event) {
    if (event.defaultPrevented) return

    const key = event.key?.toLowerCase()

    const isModifierInput = this.isInputElement(event.target)

    if ((event.metaKey || event.ctrlKey) && key === "k") {
      event.preventDefault()
      if (this.isOpen) {
        this.close()
      } else {
        this.open()
      }
      return
    }

    if (!this.isOpen && !isModifierInput && !event.metaKey && !event.ctrlKey && !event.altKey && !event.shiftKey && key === "/") {
      event.preventDefault()
      this.open()
      return
    }

    if (this.isOpen && key === "escape") {
      event.preventDefault()
      this.close()
    }

    // Prevent arrow keys from reaching other handlers when command palette is open
    if (this.isOpen && (key === "arrowleft" || key === "arrowright" || key === "arrowup" || key === "arrowdown")) {
      event.stopPropagation()
    }
  }

  handleInputKeydown(event) {
    if (!this.isOpen) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.moveSelectionBy(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.moveSelectionBy(-1)
        break
      case "Enter":
        event.preventDefault()
        this.executeSelectedCommand()
        break
      case "Tab":
        event.preventDefault()
        break
      case "Escape":
        event.preventDefault()
        this.close()
        break
      default:
        break
    }
  }

  filter() {
    if (!this.hasInputTarget) return
    const query = this.inputTarget.value || ""
    const cleaned = query.trim().toLowerCase()

    if (cleaned.length === 0) {
      this.filteredCommands = [...this.allCommands]
    } else {
      const tokens = cleaned.split(/\s+/)
      this.filteredCommands = this.allCommands.filter((command) => {
        const haystack = [
          command.label,
          command.group,
          command.description,
          command.keywords,
        ].map((value) => value ? value.toString().toLowerCase() : "").join(" ")

        return tokens.every((token) => haystack.includes(token))
      })
    }

    this.selectedIndex = 0
    this.render()
  }

  moveSelectionBy(delta, { focus = false } = {}) {
    if (!this.isOpen) return
    if (this.filteredCommands.length === 0) return

    const count = this.filteredCommands.length
    this.selectedIndex = (this.selectedIndex + delta + count) % count

    this.highlightSelection()
    if (focus) this.focusSelectedItem()
  }

  select(event) {
    const index = Number(event.currentTarget.dataset.commandIndex)
    if (Number.isNaN(index)) return

    this.selectedIndex = index
    this.executeSelectedCommand()
  }

  handleItemKeydown(event) {
    if (!this.isOpen) return

    const index = Number(event.currentTarget.dataset.commandIndex)
    if (!Number.isNaN(index)) {
      this.selectedIndex = index
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.moveSelectionBy(1, { focus: true })
        break
      case "ArrowUp":
        event.preventDefault()
        this.moveSelectionBy(-1, { focus: true })
        break
      case "Enter":
        event.preventDefault()
        this.executeSelectedCommand()
        break
      default:
        break
    }
  }

  executeSelectedCommand() {
    if (this.filteredCommands.length === 0) return
    const command = this.filteredCommands[this.selectedIndex]
    if (!command || !command.action) return

    switch (command.action.type) {
      case "state":
        this.executeStateCommand(command)
        break
      case "assignment":
        this.executeAssignmentCommand(command)
        break
      case "custom":
        this.executeCustomCommand(command)
        break
      default:
        console.warn("Unknown command type:", command.action.type)
        break
    }
  }

  async executeStateCommand(command) {
    const value = command.action.value
    if (!value) return

    if (this.isBulkMode()) {
      await this.performBulkAction({
        url: this.hasBulkStateUrlValue ? this.bulkStateUrlValue : null,
        data: { state: value },
        messageBuilder: (count) => `Changed state for ${count} workflow${count === 1 ? "" : "s"}.`,
        type: "state",
      })
      return
    }

    try {
      await this.submitUpdate({ state: value })
      this.close()
    } catch (error) {
      this.showError(error)
    }
  }

  async executeAssignmentCommand(command) {
    const value = command.action.value
    if (value === undefined || value === null) return

    if (this.isBulkMode()) {
      await this.performBulkAction({
        url: this.hasBulkAssignmentUrlValue ? this.bulkAssignmentUrlValue : null,
        data: { assigned_user_id: value },
        messageBuilder: (count) => `Updated assignment for ${count} workflow${count === 1 ? "" : "s"}.`,
        type: "assignment",
      })
      return
    }

    try {
      await this.submitUpdate({ assigned_user_id: value })
      this.close()
    } catch (error) {
      this.showError(error)
    }
  }

  async executeCustomCommand(command) {
    console.log("executeCustomCommand called", command)
    const instructions = command.action.instructions
    if (!instructions) {
      console.error("Command is missing instructions")
      this.showError("Command is missing instructions.")
      return
    }

    // Track usage for recently used sorting
    if (command.id) {
      this.trackCommandUsage(command.id)
    }

    // Extract shortcut ID from command ID (format: "command-shortcut:123" or "custom:0")
    const shortcutId = command.id && command.id.startsWith("command-shortcut:")
      ? command.id.split(":")[1]
      : null

    if (this.isBulkMode()) {
      await this.executeBulkCustomCommand(command, shortcutId)
      return
    }

    const detail = {
      message: instructions,
      submit: true,
      source: "command_palette",
      label: command.action.label || command.label,
      command_shortcut_id: shortcutId,
    }
    console.log("Event detail:", detail)

    // Find the agent-chat controller element and dispatch the event directly to it
    const agentChatElement = document.querySelector('[data-controller*="agent-chat"]')
    console.log("Agent chat element found:", agentChatElement)

    if (agentChatElement) {
      console.log("Dispatching event to agent-chat element")
      agentChatElement.dispatchEvent(new CustomEvent("agent-chat:execute", {
        detail,
        bubbles: true,
      }))
    } else {
      console.log("Agent chat element not found, using fallback")
      // Fallback to dispatching on this element
      this.element.dispatchEvent(new CustomEvent("agent-chat:execute", {
        detail,
        bubbles: true,
      }))
    }

    console.log("Closing command palette")
    this.close()
  }

  async executeBulkCustomCommand(command, shortcutId) {
    if (!this.hasBulkReplyUrlValue) {
      this.showError("Bulk command shortcuts are not available on this page.")
      return
    }

    const instructions = command.action.instructions
    if (!instructions) {
      this.showError("Command is missing instructions.")
      return
    }

    const data = {
      'paperwork_event[message]': instructions,
    }

    if (shortcutId) {
      data['paperwork_event[command_shortcut_id]'] = shortcutId
    }

    await this.performBulkAction({
      url: this.bulkReplyUrlValue,
      data,
      messageBuilder: (count) => {
        const label = command.action.label || command.label || "Command shortcut"
        const plural = count === 1 ? "" : "s"
        return `Sent "${label}" to ${count} workflow${plural}.`
      },
      type: "custom",
    })
  }

  async performBulkAction({ url, data = {}, message, messageBuilder, type }) {
    if (!this.ensureBulkSelection()) return

    if (!url) {
      this.showError("Bulk actions are not available on this page.")
      return
    }

    if (this.isExecutingCommand) return

    this.isExecutingCommand = true

    const selectionCount = Array.isArray(this.selectedProcessIds) ? this.selectedProcessIds.length : 0
    const plural = selectionCount === 1 ? "" : "s"
    const loadingMessage = selectionCount > 0 ? `Sending command to ${selectionCount} workflow${plural}...` : "Sending command..."
    this.hideStatus()
    this.showLoadingOverlay(loadingMessage)

    let encounteredError = false

    try {
      const params = new URLSearchParams()
      const dataEntries = Object.entries(data || {})
      const hasCustomIds = dataEntries.some(([key]) => key === "ids" || key === "ids[]")

      if (!hasCustomIds) {
        params.append("ids", this.selectedProcessIds.join(","))
      }

      dataEntries.forEach(([key, value]) => {
        if (value === undefined || value === null) return
        params.append(key, value)
      })

      const result = await this.postBulk(url, params)
      const count = this.selectedProcessIds.length
      const fallbackMessage = typeof messageBuilder === "function" ? messageBuilder(count) : message
      const responseMessage = result?.body?.message || fallbackMessage

      if (responseMessage) {
        this.updateOverlayMessage(responseMessage)
      }

      this.notifyBulkActionCompleted({ type, count, ids: [...this.selectedProcessIds] })

      window.setTimeout(() => {
        this.close()
      }, 600)
    } catch (error) {
      encounteredError = true
      this.showError(error)
    } finally {
      this.isExecutingCommand = false

      if (encounteredError) {
        this.hideLoadingOverlay()
      }
    }
  }

  async postBulk(url, params) {
    const response = await fetch(url, {
      method: "POST",
      headers: this.buildHeaders(),
      body: params,
      credentials: "same-origin",
    })

    return await this.handleResponse(response)
  }

  notifyBulkActionCompleted(detail = {}) {
    try {
      window.dispatchEvent(new CustomEvent("process-table:refresh-requested", {
        detail,
      }))
    } catch (error) {
      console.warn("Unable to dispatch refresh event", error)
    }
  }

  async submitUpdate(attributes) {
    if (!this.updateUrlValue) {
      throw new Error("Missing update URL")
    }

    const params = new URLSearchParams()
    Object.entries(attributes).forEach(([key, value]) => {
      if (value === undefined || value === null) return
      params.append(`paperwork_process[${key}]`, value)
    })

    const response = await fetch(this.updateUrlValue, {
      method: "PATCH",
      headers: this.buildHeaders(),
      body: params,
      credentials: "same-origin",
    })

    await this.handleResponse(response)
  }

  async handleResponse(response) {
    const contentType = response.headers.get("content-type") || ""
    const bodyText = await response.text()

    if (!response.ok) {
      throw new Error(this.extractErrorMessage(bodyText) || response.statusText)
    }

    if (contentType.includes("text/vnd.turbo-stream.html")) {
      if (window.Turbo) {
        window.Turbo.renderStreamMessage(bodyText)
      } else {
        document.documentElement.insertAdjacentHTML("beforeend", bodyText)
      }
      return { contentType, body: bodyText }
    } else if (contentType.includes("application/json")) {
      try {
        const json = bodyText ? JSON.parse(bodyText) : {}
        return { contentType, body: json, rawBody: bodyText }
      } catch (error) {
        return { contentType, body: null, rawBody: bodyText }
      }
    }

    return { contentType, body: bodyText }
  }

  showError(error) {
    const message = (error?.message || error || "Unable to run that command").toString()
    this.showStatus(message, { type: "error" })
  }

  showSuccess(message) {
    if (!message) return
    this.showStatus(message, { type: "success" })
  }

  showStatus(message, { type = "info", loading = false } = {}) {
    if (!this.hasStatusTarget) return

    const text = (message || "").toString()

    if (this.hasStatusMessageTarget) {
      this.statusMessageTarget.textContent = text
    } else {
      this.statusTarget.textContent = text
    }

    if (this.hasLoadingIndicatorTarget) {
      if (loading) {
        this.loadingIndicatorTarget.classList.remove("hidden")
      } else {
        this.loadingIndicatorTarget.classList.add("hidden")
      }
    }

    this.statusTarget.classList.remove("hidden")
    this.statusTarget.classList.remove("text-error")
    this.statusTarget.classList.remove("text-success-dark")
    this.statusTarget.setAttribute("aria-busy", loading ? "true" : "false")

    if (type === "error") {
      this.statusTarget.classList.add("text-error")
    } else if (type === "success") {
      this.statusTarget.classList.add("text-success-dark")
    }
  }

  hideStatus() {
    if (!this.hasStatusTarget) return

    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.add("hidden")
    }

    if (this.hasStatusMessageTarget) {
      this.statusMessageTarget.textContent = ""
    } else {
      this.statusTarget.textContent = ""
    }

    this.statusTarget.classList.add("hidden")
    this.statusTarget.classList.remove("text-error")
    this.statusTarget.classList.remove("text-success-dark")
    this.statusTarget.removeAttribute("aria-busy")
  }

  showLoadingOverlay(message) {
    if (this.hasOverlayMessageTarget) {
      this.overlayMessageTarget.textContent = (message || "").toString()
    }

    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove("hidden")
    }

    if (this.hasContentTarget) {
      this.contentTarget.classList.add("hidden")
    }
  }

  updateOverlayMessage(message) {
    if (this.hasOverlayMessageTarget) {
      this.overlayMessageTarget.textContent = (message || "").toString()
    }
  }

  hideLoadingOverlay() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add("hidden")
    }

    if (this.hasContentTarget) {
      this.contentTarget.classList.remove("hidden")
    }

    if (this.hasOverlayMessageTarget) {
      this.overlayMessageTarget.textContent = ""
    }
  }

  render() {
    if (!this.hasListTarget) return

    if (this.filteredCommands.length === 0) {
      this.listTarget.innerHTML = ""
      if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.remove("hidden")
      return
    }

    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add("hidden")

    let html = ""
    let lastGroup = null

    this.filteredCommands.forEach((command, index) => {
      if (command.group && command.group !== lastGroup) {
        html += `
          <div class="px-4 pt-2 pb-1 text-xs font-semibold text-ink-soft">
            ${this.escapeHtml(command.group)}
          </div>
        `
        lastGroup = command.group
      }

      const isSelected = index === this.selectedIndex
      const itemClasses = [
        "w-full",
        "text-left",
        "px-4",
        "py-2",
        // "border-b",
        "border-brand-bghighlight",
        "hover:bg-secondary/10",
        "transition",
        "focus:outline-none",
        "flex",
        "flex-col",
        "cursor-pointer",
      ]

      if (isSelected) {
        itemClasses.push("bg-secondary/10", "ring-1", "ring-secondary/40")
      }

      const badge = ""

      html += `
        <button
          type="button"
          role="option"
          aria-selected="${isSelected ? "true" : "false"}"
          tabindex="${isSelected ? "0" : "-1"}"
          class="${itemClasses.join(" ")}"
          data-command-index="${index}"
          data-action="click->command-palette#select keydown->command-palette#handleItemKeydown"
        >
          <div class="flex items-center justify-between gap-3">
            <span class="font-medium text-sm">${this.escapeHtml(command.label || "")}</span>
            ${badge}
          </div>
          <p class="text-xs text-ink-soft">${this.escapeHtml(command.description || "")}</p>
        </button>
      `
    })

    this.listTarget.innerHTML = html
    this.highlightSelection()
  }

  highlightSelection() {
    if (!this.hasListTarget) return
    const items = this.listTarget.querySelectorAll("[data-command-index]")
    items.forEach((item, index) => {
      const isSelected = index === this.selectedIndex
      item.setAttribute("aria-selected", isSelected ? "true" : "false")
      item.setAttribute("tabindex", isSelected ? "0" : "-1")

      if (isSelected) {
        item.classList.add("bg-secondary/10", "ring-1", "ring-secondary/40")
        this.scrollIntoViewIfNeeded(item)
      } else {
        item.classList.remove("bg-secondary/10", "ring-1", "ring-secondary/40")
      }
    })
  }

  focusSelectedItem() {
    const item = this.getSelectedItem()
    if (item && document.activeElement !== item) {
      item.focus({ preventScroll: true })
    }
  }

  getSelectedItem() {
    if (!this.hasListTarget) return null
    return this.listTarget.querySelector(`[data-command-index="${this.selectedIndex}"]`)
  }

  scrollIntoViewIfNeeded(element) {
    if (!element || !this.hasListTarget) return
    const parentRect = this.listTarget.getBoundingClientRect()
    const elementRect = element.getBoundingClientRect()

    if (elementRect.top < parentRect.top) {
      element.scrollIntoView({ behavior: "smooth", block: "nearest" })
    } else if (elementRect.bottom > parentRect.bottom) {
      element.scrollIntoView({ behavior: "smooth", block: "nearest" })
    }
  }

  buildHeaders() {
    const headers = {
      Accept: "text/vnd.turbo-stream.html, text/html, application/json",
    }
    if (this.csrfToken) {
      headers["X-CSRF-Token"] = this.csrfToken
    }
    headers["Content-Type"] = "application/x-www-form-urlencoded; charset=UTF-8"
    return headers
  }

  extractErrorMessage(body) {
    if (!body) return null
    try {
      const json = JSON.parse(body)
      if (json && (json.error || json.message)) {
        return json.error || json.message
      }
    } catch (error) {
      // Not JSON, attempt to strip HTML tags
    }
    const temp = document.createElement("div")
    temp.innerHTML = body
    return temp.textContent?.trim()
  }

  isInputElement(element) {
    if (!element) return false
    const tag = element.tagName?.toLowerCase()
    const editable = element.isContentEditable
    return ["input", "textarea", "select"].includes(tag) || editable
  }

  escapeHtml(value) {
    if (value === undefined || value === null) return ""
    return value
      .toString()
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }

  // Track recently used command shortcuts in localStorage
  trackCommandUsage(commandId) {
    if (!commandId || !commandId.startsWith("command-shortcut:")) return

    try {
      const storageKey = "command_palette_recent_usage"
      const recentUsage = JSON.parse(localStorage.getItem(storageKey) || "{}")

      // Store timestamp for this command
      recentUsage[commandId] = Date.now()

      // Keep only the last 50 most recent commands
      const entries = Object.entries(recentUsage)
      if (entries.length > 50) {
        entries.sort((a, b) => b[1] - a[1]) // Sort by timestamp descending
        const keep = entries.slice(0, 50)
        const cleaned = Object.fromEntries(keep)
        localStorage.setItem(storageKey, JSON.stringify(cleaned))
      } else {
        localStorage.setItem(storageKey, JSON.stringify(recentUsage))
      }
    } catch (error) {
      console.warn("Could not track command usage:", error)
    }
  }

  // Get recently used commands from localStorage
  getRecentUsage() {
    try {
      const storageKey = "command_palette_recent_usage"
      return JSON.parse(localStorage.getItem(storageKey) || "{}")
    } catch (error) {
      console.warn("Could not get recent usage:", error)
      return {}
    }
  }

  // Sort commands to show recently used shortcuts at the top
  sortCommandsByRecency(commands) {
    const recentUsage = this.getRecentUsage()
    const now = Date.now()
    const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000 // 7 days in milliseconds

    return commands.slice().sort((a, b) => {
      const aIsShortcut = a.id && a.id.startsWith("command-shortcut:")
      const bIsShortcut = b.id && b.id.startsWith("command-shortcut:")

      // If only one is a shortcut, shortcuts come first
      if (aIsShortcut && !bIsShortcut) return -1
      if (!aIsShortcut && bIsShortcut) return 1

      // If both are shortcuts, check recent usage (within last 7 days)
      if (aIsShortcut && bIsShortcut) {
        const aLastUsed = recentUsage[a.id] || 0
        const bLastUsed = recentUsage[b.id] || 0
        const aIsRecent = aLastUsed > 0 && (now - aLastUsed) < SEVEN_DAYS_MS
        const bIsRecent = bLastUsed > 0 && (now - bLastUsed) < SEVEN_DAYS_MS

        // Recently used shortcuts come first
        if (aIsRecent && !bIsRecent) return -1
        if (!aIsRecent && bIsRecent) return 1

        // Both recently used: sort by most recent
        if (aIsRecent && bIsRecent) {
          return bLastUsed - aLastUsed
        }
      }

      // Otherwise maintain original order
      return 0
    })
  }
}
